import type {
  PartialQuestionOrGroup,
  QuestionProps,
} from "../../../../../components/Questionnaire/Section/Question/types";
import {
  isAddressLookupGroup,
  isMultiselectGroup,
  isMultiselectToggleGroup,
  isPercentGroup,
  isQuestion,
  isYesNoToggleGroup,
  isAgreementType,
} from "../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../components/Questionnaire/validation/form/types";
import type { Answers, Groups } from "../../../types";
import { flattenYesNoToggleGroup } from "./yesNoToggle/yesNoToggleGroup";
import { flattenAgreementGroup } from "./agreementGroup/agreementGroup";
import { flattenMultiselectGroup } from "./multiselect/multiselectGroup";
import { flattenMultiselectToggleGroup } from "./multiselectToggle/multiselectToggleGroup";
import { flattenAddressLookupGroup } from "./addressLookup/addressLookupGroup";
import { flattenPercentGroup } from "./percentGroup/percentGroup";
import { createEmptyFlattenResult } from "./utils";

type FlattenQuestionsParams = {
  payload: PartialQuestionOrGroup[];
  answers: Answers;
  visibility?: VisibilityMap;
  groups?: Groups;
};

type FlattenQuestionsResult = {
  questions: QuestionProps[];
  answers: Answers;
  groups: Groups;
  visibility: VisibilityMap;
};

/**
 * Flattens a payload of questions and groups by converting nested question groups into flat question lists.
 * Similar to flattenSections but operates on a payload array instead of sections.
 */
export const flattenQuestions = ({
  payload,
  answers: initialAnswers,
  visibility: initialVisibility = {},
  groups: initialGroups = {},
}: FlattenQuestionsParams): FlattenQuestionsResult => {
  const answers = { ...initialAnswers };
  const visibility: VisibilityMap = { ...initialVisibility };
  const groups: Groups = { ...initialGroups };

  const flattenedQuestions: QuestionProps[] = payload.flatMap(item => {
    if (isQuestion(item)) {
      // Update visibility for standalone questions to maintain consistency with groups
      visibility[item.name] = !!item.visible;
      return [item];
    }

    let result;

    if (isYesNoToggleGroup(item)) {
      result = flattenYesNoToggleGroup(item, answers, visibility);
    } else if (isMultiselectGroup(item)) {
      result = flattenMultiselectGroup(item, answers, visibility);
    } else if (isMultiselectToggleGroup(item)) {
      result = flattenMultiselectToggleGroup(item, answers, visibility);
    } else if (isAddressLookupGroup(item)) {
      result = flattenAddressLookupGroup(item, answers, visibility);
    } else if (isPercentGroup(item)) {
      result = flattenPercentGroup(item, answers, visibility);
    } else if (isAgreementType(item)) {
      result = flattenAgreementGroup(item, answers, visibility);
    } else {
      result = createEmptyFlattenResult(answers, visibility);
    }

    Object.assign(groups, result.groups);
    Object.assign(answers, result.answers);
    Object.assign(visibility, result.visibility);

    return result.questions as QuestionProps[];
  });

  return { questions: flattenedQuestions, answers, groups, visibility };
};
